import React, { useRef } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  TextField,
  Typography,
  MenuItem,
  CircularProgress,
  Stack,
} from "@mui/material";
import {
  DatePicker,
  TimePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { FormProvider, useForm, Controller } from "react-hook-form";
import AgentSelect from "../Salary/AgentSelect";
import { useSelector } from "react-redux";
import { selectCurrentagency } from "../../../redux/features/authSlice";
import {
  usePostAttendanceMutation,
  usePutAttendanceMutation,
} from "../../../redux/api/attendance";
import AttendanceList from "./AttendanceList";
import BackButton from "../../../components/BackButton/BackButton";

const Attendance = () => {
  const currentAgency = useSelector(selectCurrentagency);
  const [postAttendance, { isLoading }] = usePostAttendanceMutation();
  const [putAttendance, { isLoading: updating }] = usePutAttendanceMutation();
  const formRef = useRef(null);
  const attendanceRefetch = useRef(null);

const methods = useForm({
  defaultValues: {
    agentid: "",
    agencyid: currentAgency?.agencyid || 0,
    atDate: dayjs(new Date()),
    geopoint: "",
    status: 0,
    intime: dayjs(),   // keep as dayjs object
    outime: dayjs(),   // keep as dayjs object
    overtime: 0,
    deduction: 0,
    attendancemethod: 0,
  },
});


  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = methods;

const onSubmit = async (data) => {
  try {
    const payload = {
      agentid: Number(data.agentid) || 0,
      agencyid: Number(data.agencyid) || 0,
      atDate: dayjs(data.atDate).toISOString(),
      geopoint: data.geopoint?.trim() || null,
      status: Number(data.status) || 0,
      intime: dayjs(data.intime).toISOString(),  // convert here
      outime: dayjs(data.outime).toISOString(),  // convert here
      deduction: Number(data.deduction) || 0,
      overtime: Number(data.overtime) || 0,
      attendancemethod: Number(data.attendancemethod) || 0,
    };

    if (data.id) {
      await putAttendance({ id: data.id, ...payload }).unwrap();
    } else {
      await postAttendance(payload).unwrap();
    }

    reset();
    if (attendanceRefetch.current) attendanceRefetch.current();
  } catch (err) {
    console.error("Submit failed:", err);
  }
};



  const handleEdit = (row) => {
    reset({
      id: row.id,
      agentid: row.agentid,
      agencyid: row.agencyid,
      atDate: dayjs(row.atDate),
      geopoint: row.geopoint || "",
      status: row.status || 0,
      intime: row.intime ? dayjs(row.intime) : dayjs(),
      outime: row.outime ? dayjs(row.outime) : dayjs(),
      deduction: row.deduction || 0,
      overtime: row.overtime || 0,
      attendancemethod: row.attendancemethod || 0,
    });
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <Box sx={{ px: 3 }}>
        <Box sx={{ mb: 5 }}>
          <Stack alignItems="start" spacing={2}>
            <BackButton />
          </Stack>
        </Box>
        <Typography variant="h3">Attendance</Typography>
      </Box>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Container maxWidth="sm">
          <Box ref={formRef} sx={{ mt: 3, p: 3, borderRadius: 2, boxShadow: 3 }}>
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2}>
                  {/* Agent Select */}
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="agentid"
                      control={control}
                      rules={{ required: "Agent is required" }}
                      render={({ field, fieldState: { error } }) => (
                        <AgentSelect
                          agencyId={currentAgency.agencyid}
                          value={field.value}
                          onChange={field.onChange}
                          error={!!error}
                          helperText={error ? error.message : ""}
                        />
                      )}
                    />
                  </Grid>

                  {/* Date Picker */}
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="atDate"
                      control={control}
                      rules={{ required: "Date is required" }}
                      render={({ field }) => (
                        <DatePicker
                          label="At Date"
                          value={field.value}
                          onChange={(val) => field.onChange(val)}
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                              error: !!errors.atDate,
                              helperText: errors.atDate?.message,
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {/* In Time */}
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="intime"
                      control={control}
                      rules={{ required: "In Time is required" }}
                      render={({ field }) => (
                        <TimePicker
                          label="In Time"
                          value={field.value}
                          onChange={(val) => field.onChange(val)}
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                              error: !!errors.intime,
                              helperText: errors.intime?.message,
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Out Time */}
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="outime"
                      control={control}
                      rules={{ required: "Out Time is required" }}
                      render={({ field }) => (
                        <TimePicker
                          label="Out Time"
                          value={field.value}
                          onChange={(val) => field.onChange(val)}
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                              error: !!errors.outime,
                              helperText: errors.outime?.message,
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Geopoint */}
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="geopoint"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          size="small"
                          {...field}
                          label="Geo Point (optional)"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>

                  {/* Status */}
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="status"
                      control={control}
                      rules={{ required: "Status is required" }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Status"
                          select
                          size="small"
                          fullWidth
                          error={!!errors.status}
                          helperText={errors.status?.message}
                        >
                          <MenuItem value={1}>Present</MenuItem>
                          <MenuItem value={2}>Absent</MenuItem>
                          <MenuItem value={3}>Leave</MenuItem>
                        </TextField>
                      )}
                    />
                  </Grid>

                  {/* Overtime */}
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="overtime"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type="number"
                          size="small"
                          label="Overtime"
                          fullWidth
                          inputProps={{ min: 0 }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Deduction */}
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="deduction"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type="number"
                          size="small"
                          label="Deduction"
                          fullWidth
                          inputProps={{ min: 0 }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Attendance Method */}
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="attendancemethod"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Attendance Method"
                          select
                          size="small"
                          fullWidth
                        >
                          <MenuItem value={0}>Manual</MenuItem>
                          <MenuItem value={1}>Machine</MenuItem>
                          <MenuItem value={2}>Geo</MenuItem>
                        </TextField>
                      )}
                    />
                  </Grid>
                </Grid>

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{ mt: 3, color: "white" }}
                  disabled={isLoading || updating}
                  startIcon={
                    isLoading || updating ? <CircularProgress size={20} /> : null
                  }
                >
                  {isLoading || updating ? "Submitting..." : "Submit"}
                </Button>
              </form>
            </FormProvider>
          </Box>
        </Container>
      </LocalizationProvider>

      <AttendanceList
        onEdit={handleEdit}
        onRefetch={(fn) => (attendanceRefetch.current = fn)}
      />
    </>
  );
};

export default Attendance;
